// UserManager.js
import axios from "axios";
import config from "../../config";

const BASE_URL = `${config.url}/api/auth`;

/**
 * Service for user registration.
 * @typedef {{ firstName: string, lastName: string, email: string, password: string }} UserData
 * @typedef {import("../dto/UserDTO")} UserDTO
 */

const UserManager = {
  /**
   * Registers a new user by sending their details to the backend.
   * @param {UserData} userData
   * @returns {Promise<UserDTO>} The registered user DTO.
   */
  async registerUser(userData) {
    try {
      const { data } = await axios.post(`${BASE_URL}/register`, userData);
      // ApiResponse<UserDTO> assumed shape: { responseCode: number, data: UserDTO }
      return data.data;
    } catch (error) {
      // normalize error
      const msg = error.response?.data?.message || error.message;
      throw new Error(msg);
    }
  },
  /**
 * Fetches all users from the backend.
 * @returns {Promise<UserDTO[]>} List of users.
 */
async getAllUsers() {
  try {
    const { data } = await axios.get(`${BASE_URL}/users`);
    return data.responseData;
  } catch (error) {
    const msg = error.response?.data?.message || error.message;
    throw new Error(msg);
  }
},

/**
   * Fetches details of a single user by ID.
   * @param {number} userId
   * @returns {Promise<UserDTO>}
   */
async getUserDetailsById(userId) {
  try {
    const { data } = await axios.get(`${BASE_URL}/user/${userId}`);
    return data.responseData;
  } catch (error) {
    const msg = error.response?.data?.message || error.message;
    throw new Error(msg);
  }
},

/**
 * Searches users by query string.
 * @param {string} query - The search query (e.g. name, email, etc.).
 * @returns {Promise<UserDTO[]>} List of matched users.
 */
async searchUsers(query) {
  try {
    const { data } = await axios.get(`${config.url}/api/users/search-with-subscription`, {
      params: { query },
    });
    return data;
  } catch (error) {
    const msg = error.response?.data?.message || error.message;
    throw new Error(msg);
  }
},

/**
 * Updates user information.
 * @param {number} userId - The ID of the user to update
 * @param {Object} userData - The user data to update
 * @returns {Promise<UserDTO>} The updated user DTO.
 */
async updateUser(userId, userData) {
  try {
    // Add userId to the userData object since the backend expects it
    const userDataWithId = {
      ...userData,
      userId: userId
    };
    
    console.log("UserManager - Sending to backend:", userDataWithId);
    
    const { data } = await axios.post(`${BASE_URL}/update_user_details`, userDataWithId);
    
    console.log("UserManager - Backend response:", data);
    
    return data.responseData || data.data;
  } catch (error) {
    console.error("UserManager - Error details:", error.response);
    const msg = error.response?.data?.message || error.message;
    throw new Error(msg);
  }
},

/**
 * Fetches all users with their subscription information.
 * @returns {Promise<UserWithSubscriptionDTO[]>} List of users with subscription details.
 */
async getAllUsersWithSubscriptionInfo() {
  try {
    const { data } = await axios.get(`${config.url}/api/users/all-with-subscription`);
    return data;
  } catch (error) {
    const msg = error.response?.data?.message || error.message;
    throw new Error(msg);
  }
},


};

export default UserManager;